/* 2026-10 — Admin Loom bridge banner.
 *
 * Persistent (non-dismissible) notice shown whenever the signed-in
 * writer session is the permanent "Admin Troubleshooting" sandbox
 * account (`user.is_admin_sandbox`), minted automatically for any
 * logged-in admin visiting /app. "Exit to Admin" logs out of the
 * sandbox writer session and returns to /admin.
 */
import React from 'react';
import { ShieldAlert, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth_context';
import { authApi } from '@/lib/auth_api';

export default function AdminModeBanner() {
  const { user } = useAuth();
  if (!user?.is_admin_sandbox) return null;

  // Call the API directly (not the context's `logout`, which calls
  // setUser(null) and re-renders RequireAuth -> briefly flashes /login
  // before this hard navigation lands). Firing the hard nav immediately
  // skips that flash; the logout request still completes in-flight.
  const exitToAdmin = () => {
    authApi.logout().catch(() => { /* ignore — navigating away regardless */ });
    window.location.href = '/admin';
  };

  return (
    <div
      data-testid="admin-mode-banner"
      className="flex h-9 shrink-0 items-center justify-between gap-3 border-b border-amber-500/40 bg-gradient-to-r from-amber-500/25 via-amber-500/10 to-amber-500/25 px-3 text-[12px]"
    >
      <div className="flex min-w-0 items-center gap-2">
        <ShieldAlert className="h-3.5 w-3.5 shrink-0 text-amber-600" />
        <span className="truncate">
          <b>Admin Mode — Troubleshooting</b>
          <span className="hidden text-muted-foreground md:inline">
            {' · '}sandbox account, separate from real writer data
          </span>
        </span>
      </div>
      <Button
        size="sm"
        variant="ghost"
        className="h-7 gap-1.5 px-3 text-[11.5px] text-amber-800 hover:bg-amber-500/20"
        onClick={exitToAdmin}
        data-testid="admin-mode-exit"
      >
        <LogOut className="h-3 w-3" /> Exit to Admin
      </Button>
    </div>
  );
}
